/**
 * 虚拟列表纯逻辑核心（零 DOM 依赖，可单测）。DOM 接线在 7b 的 VirtualList 组件。
 *
 * 核心口径（替代旧版 5 计数器 + 3 套暂存区的滚动状态机，legacy §5.8 #11）：
 * **整个列表只有一个 anchor 状态**，它是滚动位置的唯一事实源——
 * - `{ kind: 'bottom' }`：粘底（follow）模式。聊天窗口的初始态；新消息 append、
 *   任何测高/插入变化后视口都钉在最底部（反向滚动的「底部优先」语义）。
 * - `{ kind: 'item', key, offset }`：用户在看历史。anchor 项的顶边相对视口顶边
 *   偏移 offset px（项顶在视口顶上方时为负）。此后 prepend 历史、任意行测高变化，
 *   一律通过 reconcile() 重算 scrollTop 把 anchor 项钉回原位——视觉零跳动。
 *
 * 数据流（7b 按此接线）：
 * - 用户滚动 → setScrollTop(px)：从滚动位置重新推导 anchor（进入距底
 *   followThresholdPx 区间 → 回到 bottom 模式）。幂等：视图没动（钳后值==当前
 *   scrollTop）直接保留现有 anchor——程序滚动的回声零副作用，且 scrollToKey 被
 *   钳到边界时锚定的目标行不会被回声换成边界行（14.6b 实证：换掉后 prepend 补
 *   偿会钉住边界行，跳转目标被挤出视口）。
 * - 数据/尺寸变化（setItems / measureMany / setViewportHeight）→ reconcile()：
 *   按 anchor 算出期望 scrollTop，变了就记 pendingScrollTop，DOM 层
 *   takePendingScrollTop() 取走并写回 element.scrollTop。
 * - 布局 = 按 key 的测高缓存（未测用 estimateHeight），前缀和数组 dirtyFrom
 *   起懒重算；10k 项全量重算是一次加法循环，可忽略。
 *
 * anchor 是可序列化的纯数据：任务 13 冷启动恢复滚动位置 = 存 getAnchor()、
 * 回放 restoreAnchor()。
 */

export type VirtualAnchor =
  | { kind: 'bottom' }
  | { kind: 'item'; key: string; offset: number };

export interface VirtualizerOptions {
  /** 未测量行的估计高度（px）。 */
  estimateHeight: number;
  /** 可视范围上下各外扩的像素（惯性滚动防白屏的第一道闸，7c 实测调参）。 */
  overscanPx?: number;
  /** 滚动位置距底不超过该值视为回到粘底模式。 */
  followThresholdPx?: number;
}

export interface VirtualItemPos {
  readonly key: string;
  /** 该行顶边在整个列表坐标系里的位置（px）。 */
  readonly top: number;
}

export interface VirtualSnapshot {
  readonly totalHeight: number;
  readonly scrollTop: number;
  /** true = 粘底模式（「滚到底」按钮据此隐藏）。 */
  readonly follow: boolean;
  /** 应渲染的区间 [start, end)（含 overscan，已夹进 [0, 项数]）。 */
  readonly start: number;
  readonly end: number;
  readonly items: readonly VirtualItemPos[];
}

export interface Virtualizer {
  setViewportHeight(px: number): void;
  /** 整表替换（调用方保证 key 唯一且时间线升序）。anchor 项被删时就近顺延，全删回 bottom。 */
  setItems(keys: readonly string[]): void;
  /** 批量上报实测行高（一次 reconcile）。未知 key（已卸载的行）忽略。 */
  measureMany(entries: ReadonlyArray<readonly [key: string, height: number]>): void;
  /** 用户滚动（DOM scroll 事件）。越界值（iOS 橡皮筋）先夹再推导 anchor。 */
  setScrollTop(px: number): void;
  /** 程序期望的 scrollTop（取走即清）。null = 无需修正。 */
  takePendingScrollTop(): number | null;
  scrollToBottom(): void;
  /**
   * 跳转到指定行（14.6b 引用跳转）：目标已完整可见则不滚动；否则把目标顶边
   * 锚到视口上 1/3 处——anchor 即目标行，后续测高修正保持其钉在原位。
   * 返回 false = key 不存在（调用方走降级提示）。
   */
  scrollToKey(key: string): boolean;
  getAnchor(): VirtualAnchor;
  /** 恢复持久化的 anchor；key 已不存在则回 bottom。 */
  restoreAnchor(anchor: VirtualAnchor): void;
  snapshot(): VirtualSnapshot;
}

const DEFAULT_OVERSCAN_PX = 480;
const DEFAULT_FOLLOW_THRESHOLD_PX = 32;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function createVirtualizer(options: VirtualizerOptions): Virtualizer {
  const estimate = options.estimateHeight;
  const overscan = options.overscanPx ?? DEFAULT_OVERSCAN_PX;
  const followThreshold = options.followThresholdPx ?? DEFAULT_FOLLOW_THRESHOLD_PX;

  let keys: string[] = [];
  let indexByKey = new Map<string, number>();
  const heights = new Map<string, number>();
  // prefix[i] = 第 i 项的顶边位置，prefix[keys.length] = 总高；[0, dirtyFrom] 区间有效
  const prefix: number[] = [0];
  let dirtyFrom = 0;
  let viewport = 0;
  let scrollTop = 0;
  let anchor: VirtualAnchor = { kind: 'bottom' };
  let pendingScrollTop: number | null = null;

  function heightOf(i: number): number {
    return heights.get(keys[i]!) ?? estimate;
  }

  /** 填充前缀和至 prefix[upTo] 有效（即第 upTo 项的顶边可用）。 */
  function ensurePrefix(upTo: number): void {
    for (let i = dirtyFrom; i < upTo; i++) {
      prefix[i + 1] = prefix[i]! + heightOf(i);
    }
    if (upTo > dirtyFrom) dirtyFrom = upTo;
  }

  function totalHeight(): number {
    ensurePrefix(keys.length);
    return keys.length === 0 ? 0 : prefix[keys.length]!;
  }

  /** 包含纵坐标 y 的项下标（y 越界时夹到首/末项）。调用前须 ensurePrefix 到位。 */
  function indexAt(y: number): number {
    let lo = 0;
    let hi = keys.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (prefix[mid]! <= y) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  /** 按 anchor 重算期望 scrollTop；变了记 pendingScrollTop 等 DOM 层取走。 */
  function reconcile(): void {
    const total = totalHeight();
    const maxScroll = Math.max(0, total - viewport);
    let desired: number;
    if (anchor.kind === 'bottom') {
      desired = maxScroll;
    } else {
      const idx = indexByKey.get(anchor.key);
      if (idx === undefined) {
        // setItems/restoreAnchor 已兜底，理论不可达；防御性回 bottom
        anchor = { kind: 'bottom' };
        desired = maxScroll;
      } else {
        desired = clamp(prefix[idx]! - anchor.offset, 0, maxScroll);
      }
    }
    if (desired !== scrollTop) {
      scrollTop = desired;
      pendingScrollTop = desired;
    }
  }

  return {
    setViewportHeight(px) {
      if (px === viewport) return;
      viewport = px;
      reconcile();
    },

    setItems(next) {
      const prev = keys;
      const prevIndexByKey = indexByKey;
      let d = 0;
      const minLen = Math.min(prev.length, next.length);
      while (d < minLen && prev[d] === next[d]) d++;
      keys = next.slice();
      indexByKey = new Map();
      for (let i = 0; i < keys.length; i++) indexByKey.set(keys[i]!, i);
      dirtyFrom = Math.min(dirtyFrom, d);
      if (anchor.kind === 'item' && !indexByKey.has(anchor.key)) {
        // anchor 项被删：先向后再向前找最近的幸存项（视觉上基本原位），都没有回 bottom
        const oldIdx = prevIndexByKey.get(anchor.key);
        let fallback: string | undefined;
        if (oldIdx !== undefined) {
          for (let i = oldIdx + 1; i < prev.length && fallback === undefined; i++) {
            if (indexByKey.has(prev[i]!)) fallback = prev[i];
          }
          for (let i = oldIdx - 1; i >= 0 && fallback === undefined; i--) {
            if (indexByKey.has(prev[i]!)) fallback = prev[i];
          }
        }
        anchor = fallback !== undefined ? { kind: 'item', key: fallback, offset: anchor.offset } : { kind: 'bottom' };
      }
      reconcile();
    },

    measureMany(entries) {
      let changed = false;
      for (const [key, height] of entries) {
        const idx = indexByKey.get(key);
        if (idx === undefined) continue;
        const prevHeight = heights.get(key) ?? estimate;
        heights.set(key, height);
        if (height !== prevHeight) {
          dirtyFrom = Math.min(dirtyFrom, idx);
          changed = true;
        }
      }
      if (changed) reconcile();
    },

    setScrollTop(px) {
      pendingScrollTop = null;
      const total = totalHeight();
      const maxScroll = Math.max(0, total - viewport);
      const next = clamp(px, 0, maxScroll);
      if (next === scrollTop) return; // 视图没动：保留现有 anchor（回声/重复事件）
      scrollTop = next;
      if (keys.length === 0 || scrollTop >= maxScroll - followThreshold) {
        anchor = { kind: 'bottom' };
        return;
      }
      const i = indexAt(scrollTop);
      anchor = { kind: 'item', key: keys[i]!, offset: prefix[i]! - scrollTop };
    },

    takePendingScrollTop() {
      const v = pendingScrollTop;
      pendingScrollTop = null;
      return v;
    },

    scrollToBottom() {
      anchor = { kind: 'bottom' };
      reconcile();
    },

    scrollToKey(key) {
      const idx = indexByKey.get(key);
      if (idx === undefined) return false;
      ensurePrefix(idx + 1);
      const top = prefix[idx]!;
      if (top >= scrollTop && top + heightOf(idx) <= scrollTop + viewport) return true;
      anchor = { kind: 'item', key, offset: Math.round(viewport / 3) };
      reconcile();
      return true;
    },

    getAnchor() {
      return anchor;
    },

    restoreAnchor(next) {
      anchor = next.kind === 'item' && !indexByKey.has(next.key) ? { kind: 'bottom' } : next;
      reconcile();
    },

    snapshot() {
      const total = totalHeight();
      const follow = anchor.kind === 'bottom';
      if (keys.length === 0) {
        return { totalHeight: 0, scrollTop, follow, start: 0, end: 0, items: [] };
      }
      const start = indexAt(Math.max(0, scrollTop - overscan));
      const last = indexAt(scrollTop + viewport + overscan);
      const items: VirtualItemPos[] = [];
      for (let i = start; i <= last; i++) {
        items.push({ key: keys[i]!, top: prefix[i]! });
      }
      return { totalHeight: total, scrollTop, follow, start, end: last + 1, items };
    },
  };
}
